import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Form, Card, Row, Col, Alert } from 'react-bootstrap';
import InputNumber from './InputNumber';
import CheckBoxInput from './CheckBoxInput';
import RadioInput from './RadioInput';
import formInputsTypes from 'constants/simulatorData/simulatorConstant';

const SimulatorMotor = ({
	currentFormValue,
	setCurrentFormValue,
	simulatorData,
	scrollToBottom,
}) => {
	const [pendingScrollStepId, setPendingScrollStepId] = useState(null);
	const [dataForm, setDataForm] = useState(simulatorData);

	const scrollToStep = useCallback((stepId) => {
		if (!stepId) return;

		// your wrapper will have id="step-<data.id>"
		const el = document.getElementById(`step-${stepId}`);
		if (!el) return;

		el.scrollIntoView({
			behavior: 'smooth',
			block: 'start',
			inline: 'nearest',
		});
	}, []);

	const isStepCompleted = (step, values) => {
		if (!step?.name) return false;
		const v = values?.[step.name];
		if (v === undefined || v === null) return false;

		// If you store { value: ... }
		if (typeof v === 'object' && 'value' in v) {
			return v.value !== undefined && v.value !== null && v.value !== '';
		}
		return true;
	};

	const findFirstIncompleteId = (step, values) => {
		let node = step;
		while (node) {
			if (!isStepCompleted(node, values)) return node.id;
			node = node.nextStep;
		}
		return null;
	};


	useEffect(() => {
		// priority 1: scroll to the new step that just appeared
		if (pendingScrollStepId) {
			requestAnimationFrame(() => scrollToStep(pendingScrollStepId));
			return;
		}

		// ✅ Ne pas auto-scroll pendant la saisie dans un input/textarea/select
		const active = document.activeElement;
		const isTyping =
			active &&
			(active.tagName === 'INPUT' ||
				active.tagName === 'TEXTAREA' ||
				active.tagName === 'SELECT' ||
				active.isContentEditable);

		if (isTyping) return;

		// priority 2: otherwise, focus the first incomplete step
		const firstIncomplete = findFirstIncompleteId(dataForm, currentFormValue?.values);
		if (firstIncomplete) {
			requestAnimationFrame(() => scrollToStep(firstIncomplete));
		}
	}, [pendingScrollStepId, dataForm, currentFormValue?.values, scrollToStep]);


	const tabInputs = [];
	console.log('currentFormValue', currentFormValue);
	const makeid = (length) => {
		let result = '';
		const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
		const charactersLength = characters.length;
		let counter = 0;
		while (counter < length) {
			result += characters.charAt(Math.floor(Math.random() * charactersLength));
			counter += 1;
		}
		return result;
	};

	const injectObject = (object, injectedObject, targetIndex, index = 0) => {
		let newObject = object;
		if (index === targetIndex) {
			newObject = {
				...object,
				...injectedObject,
			};
		} else if (object.nextStep) {
			newObject = {
				...object,
				nextStep: {
					...injectObject(object.nextStep, injectedObject, targetIndex, index + 1),
				},
			};
		}
		return newObject;
	};

	const upsertOrdered = (ordered = [], key, value, order, originalValue) => {
		const idx = ordered.findIndex((x) => x.key === key);
		if (idx === -1) return [...ordered, { key, value, order,originalValue }]; // first time -> append
		// update value but keep original order
		const next = [...ordered];
		next[idx] = { ...next[idx], value , originalValue};
		return next;
	};
	const PRICE_M2 = {
		BP55: { white: 216.68, color: 241.35 },
		STANDARD: { white: 113.4, color: 168.3 },
	};

	const mmToM = (mm) => (Number(mm) || 0) / 1000;

	const calcTablierPrice = ({ widthMm, heightMm, priceGroup, isWhite }) => {
		const w = mmToM(widthMm);
		const h = mmToM(heightMm);
		const area = w * h; // m²

		const group = PRICE_M2[priceGroup || 'STANDARD'] || PRICE_M2.STANDARD;
		const unit = isWhite ? group.white : group.color;

		const total = area * unit;

		// arrondi 2 décimales
		return {
			areaM2: Math.round(area * 1000) / 1000, // ex: 1.234 m²
			unitPriceM2: unit,
			total: Math.round(total * 100) / 100,
		};
	};
	const trimOrderedAfterKey = (ordered = [], key) => {
		const idx = ordered.findIndex((x) => x.key === key);
		if (idx === -1) return ordered;          // key not found => no trimming
		return ordered.slice(0, idx + 1);        // keep up to key (included)
	};

	const handleSave = (func, data, nextStep, option) => {
		func(nextStep);
		const nextStepIsOptionalCheckbox =
			nextStep?.type === formInputsTypes?.CHECKBOX_INPUT;
		console.log("***option", option)
		setCurrentFormValue((prev) => {
			const key = data.name;
			const value = option?.description;
			const originalValue = option?.meta?.value;

			// ✅ 1) If key already exists in ordered, remove everything after it
			const trimmedOrdered = trimOrderedAfterKey(prev?.ordered ?? [], key);

			// ✅ 2) Upsert the current key/value into the trimmed list
			// Use existing order if exists, else create new
			const existingOrder = prev?.orders?.[key];
			const order = existingOrder ?? orderRef.current++;

			const nextOrders = { ...(prev?.orders ?? {}), [key]: order };
			const nextOrdered = upsertOrdered(trimmedOrdered, key, value, order, originalValue);

			// ✅ 3) Values: delete values that are no longer in ordered (tail removed)
			// Build a set of allowed keys (keys still present in nextOrdered)
			const allowedKeys = new Set(nextOrdered.map((x) => x.key));

			// Keep only allowed values + always set current key
			const prevValues = prev?.values ?? {};
			const nextValues = Object.keys(prevValues).reduce((acc, k) => {
				if (allowedKeys.has(k)) acc[k] = prevValues[k];
				return acc;
			}, {});
			nextValues[key] = option?.meta; // set current

			// Also clean orders map to avoid zombie orders
			const prevOrders = prev?.orders ?? {};
			const cleanedOrders = Object.keys(prevOrders).reduce((acc, k) => {
				if (allowedKeys.has(k) || k === key) acc[k] = prevOrders[k];
				return acc;
			}, {});
			cleanedOrders[key] = order;

			// ---- Pricing (same logic, but now uses cleaned nextValues) ----
			const widthMm = nextValues?.largeur?.value;
			const heightMm = nextValues?.hauteur?.value;
			const priceGroup = nextValues?.lameTaille?.priceGroup;
			const isWhite = nextValues?.['Couleur de lame']?.isWhite || false;

			let tablierPricing = prev?.tablierPricing;
			if (widthMm && heightMm && priceGroup && typeof isWhite === 'boolean') {
				tablierPricing = calcTablierPrice({ widthMm, heightMm, priceGroup, isWhite });
			} else {
				tablierPricing = undefined; // avoid stale price
			}

			const productFinal = nextOrdered
				.map((x) => x.value)
				.filter(Boolean)
				.join('\n\n');
			console.log("*****", productFinal, nextOrdered)
			return {
				...prev,
				values: nextValues,
				orders: cleanedOrders,
				ordered: nextOrdered,
				complet: !nextStep || nextStepIsOptionalCheckbox,
				price: tablierPricing?.total,
				tablierPricing,
				productFinal,
			};
		});
	};

	const orderRef = useRef(0);

	const switchInputs = (data, func) => {
		if (!data?.type) return;

		switch (data.type) {
			case formInputsTypes.RADIO_INPUT:
			case formInputsTypes.SELECT_INPUT:
				tabInputs.push(
					<div key={data.id} className='mb-4' id={`step-${data.id}`}>
						<RadioInput
							data={data}
							currentFormValue={currentFormValue}
							handelChange={(nextStep, value) => {
								handleSave(func, data, nextStep, value);
							}}
						/>
					</div>,
				);
				break;

			case formInputsTypes.CHECKBOX_INPUT:
				tabInputs.push(
					<div key={data.id} className='mb-4' id={`step-${data.id}`}>
						<CheckBoxInput
							data={data}
							currentFormValue={currentFormValue}
							handelChange={(nextStep, value) => {
								handleSave(func, data, nextStep, value);
							}}
						/>
					</div>,
				);
				break;

			case formInputsTypes.INPUT_NUMBER:
			case formInputsTypes.INPUT_TEXT:
				tabInputs.push(
					<div key={data.id} className='mb-4' id={`step-${data.id}`}>
						<InputNumber
							autoFocus={true}
							data={data}
							handelChange={(nextStep, value) => {
								handleSave(func, data, nextStep, value);
							}}
						/>
					</div>,
				);
				break;

			default:
				console.warn(`Unknown input type: ${data.type}`);
				console.log('********', formInputsTypes.RADIO_INPUT, data.type);

				break;
		}
	};

	const createElementsForm = (object, index = 0) => {
		if (!object) return;
		// ✅ Si l'objet courant est un input => pas de scroll

		switchInputs(object, (data) => {

			const newId = `${data?.id}_${makeid(5)}`;
			const isInput =
				object?.type === formInputsTypes?.INPUT_NUMBER ||
				object?.type === formInputsTypes?.INPUT_TEXT
			if (!!!isInput) {
				setPendingScrollStepId(newId);
				setTimeout(() => setPendingScrollStepId(null), 400);
			}

			console.log("isInput", isInput, pendingScrollStepId)

			setDataForm(
				injectObject(
					dataForm,
					{
						nextStep: data ? { ...data, id: newId } : false,
					},
					index,
				),
			);
		});

		if (object.nextStep) {
			createElementsForm(object.nextStep, index + 1);
		}
	};

	createElementsForm(dataForm);

	return (
		<div className='row'>
			{tabInputs.length ? (
				tabInputs
			) : (
				<Alert variant='info' className='text-center mb-0'>
					Aucune donnée définie
				</Alert>
			)}
		</div>
	);
};

SimulatorMotor.propTypes = {
	currentFormValue: PropTypes.object.isRequired,
	setCurrentFormValue: PropTypes.func.isRequired,
	simulatorData: PropTypes.object,
	scrollToBottom: PropTypes.func,
};

SimulatorMotor.defaultProps = {
	simulatorData: {},
	scrollToBottom: () => { },
};

export default SimulatorMotor;
